
import { Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';

const CURRENT_YEAR = new Date().getFullYear();

export function FooterSection() {
  const { isDark } = useTheme();
  
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <footer className={`relative py-20 border-t transition-colors duration-300 ${
      isDark 
        ? 'bg-black border-gray-800' 
        : 'bg-white border-gray-200'
    }`}>
      <div className="container mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          {/* Company Info */}
          <div className="md:col-span-2">
            <h3 className="text-3xl font-light mb-4">
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Driscop</span>
            </h3>
            <p className={`mb-6 max-w-md font-light transition-colors duration-300 ${
              isDark ? 'text-gray-400' : 'text-gray-400'
            }`}>
              AI-powered cybersecurity for UK SMEs. Protecting small businesses from phishing and smishing attacks with affordable, enterprise-grade security.
            </p>
            <div className={`flex items-center gap-4 text-sm transition-colors duration-300 ${
              isDark ? 'text-gray-400' : 'text-gray-400'
            }`}>
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-line text-blue-400"></i>
                <span>Manchester, UK</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-mail-line text-blue-400"></i>
                <span>hello@driscop.co.uk</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className={`font-medium mb-4 tracking-wide transition-colors duration-300 ${
              isDark ? 'text-white' : 'text-white'
            }`}>Solutions</h4>
            <div className="space-y-2">
              <button 
                onClick={() => scrollToSection('how-it-works')}
                className={`block transition-colors duration-300 cursor-pointer text-left ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                How It Works
              </button>
              <button 
                onClick={() => scrollToSection('solutions')}
                className={`block transition-colors duration-300 cursor-pointer text-left ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                AI Threat Detection
              </button>
              <button 
                onClick={() => scrollToSection('solutions')}
                className={`block transition-colors duration-300 cursor-pointer text-left ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Employee Training
              </button>
              <button 
                onClick={() => scrollToSection('solutions')}
                className={`block transition-colors duration-300 cursor-pointer text-left ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                SME Dashboard
              </button>
            </div>
          </div>

          {/* Company */}
          <div>
            <h4 className={`font-medium mb-4 tracking-wide transition-colors duration-300 ${
              isDark ? 'text-white' : 'text-white'
            }`}>Company</h4>
            <div className="space-y-2">
              <button 
                onClick={() => scrollToSection('about')}
                className={`block transition-colors duration-300 cursor-pointer text-left ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                About Us
              </button>
              <button 
                onClick={() => scrollToSection('team')}
                className={`block transition-colors duration-300 cursor-pointer text-left ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Our Team
              </button>
              <button 
                onClick={() => scrollToSection('pricing')}
                className={`block transition-colors duration-300 cursor-pointer text-left ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Pricing
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className={`block transition-colors duration-300 cursor-pointer text-left ${
                  isDark 
                    ? 'text-gray-400 hover:text-white' 
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                Contact
              </button>
            </div>
          </div>
        </div>

        {/* Trust Badges */}
        <div className={`border-t pt-8 mb-8 transition-colors duration-300 ${
          isDark ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <div className={`flex flex-wrap justify-center gap-8 text-sm transition-colors duration-300 ${
            isDark ? 'text-gray-400' : 'text-gray-400'
          }`}>
            <div className="flex items-center gap-2">
              <i className="ri-shield-check-line text-green-400"></i>
              <span>GDPR Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-lock-line text-green-400"></i>
              <span>ISO 27001 Certified</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-award-line text-green-400"></i>
              <span>Cyber Essentials Plus</span>
            </div>
            <div className="flex items-center gap-2">
              <i className="ri-map-2-line text-green-400"></i>
              <span>UK Data Protection</span>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="flex justify-center space-x-6 mb-8">
          <a href="#" className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
            isDark 
              ? 'bg-gray-800 text-gray-400 hover:text-white hover:bg-blue-600' 
              : 'bg-gray-100 text-gray-400 hover:text-white hover:bg-blue-600'
          }`}>
            <i className="ri-linkedin-fill text-lg"></i>
          </a>
          <a href="#" className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
            isDark 
              ? 'bg-gray-800 text-gray-400 hover:text-white hover:bg-blue-600' 
              : 'bg-gray-100 text-gray-400 hover:text-white hover:bg-blue-600'
          }`}>
            <i className="ri-twitter-x-fill text-lg"></i>
          </a>
          <a href="#" className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 cursor-pointer ${
            isDark 
              ? 'bg-gray-800 text-gray-400 hover:text-white hover:bg-blue-600' 
              : 'bg-gray-100 text-gray-400 hover:text-white hover:bg-blue-600'
          }`}>
            <i className="ri-youtube-fill text-lg"></i>
          </a>
        </div>

        {/* Statutory disclosures + Copyright */}
        <div className={`text-center border-t pt-8 transition-colors duration-300 ${
          isDark ? 'border-gray-800' : 'border-gray-200'
        }`}>
          <p className={`text-xs font-light mb-4 transition-colors duration-300 ${
            isDark ? 'text-gray-500' : 'text-gray-500'
          }`}>
            Driscop Ltd is a company registered in England and Wales. Company number: 16487052.
            Registered office: 6 Hall Grove, Manchester, M14 5HU.
          </p>
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <span className={`text-sm font-light transition-colors duration-300 ${
              isDark ? 'text-gray-400' : 'text-gray-400'
            }`}>
              © {CURRENT_YEAR} Driscop Ltd. All rights reserved.
            </span>
            <div className={`flex gap-6 text-sm font-light transition-colors duration-300 ${
              isDark ? 'text-gray-400' : 'text-gray-400'
            }`}>
              <Link to="/privacy" className={`transition-colors duration-300 cursor-pointer ${
                isDark ? 'hover:text-white' : 'hover:text-white'
              }`}>Privacy Policy</Link>
              <Link to="/terms" className={`transition-colors duration-300 cursor-pointer ${
                isDark ? 'hover:text-white' : 'hover:text-white'
              }`}>Terms of Service</Link>
              <Link to="/cookies" className={`transition-colors duration-300 cursor-pointer ${
                isDark ? 'hover:text-white' : 'hover:text-white'
              }`}>Cookie Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
